import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import { onecSavePriceFilter } from '../api/pricingApi'
import { EMPTY_PRICE_LIST_INTENT, PRICE_LIST_MAX_SEARCH } from './priceList'
import type { CommandAttempt, PairDraft, PriceListIntent, PricingIntentState } from './types'

/**
 * Намерение человека в ценах и остатках: что набрал и что запросил. Ответов
 * сервера здесь НЕТ — они в кэше RTK Query (R01). Каждая запись принадлежит
 * rid: смена базы не подставляет набранное в чужую (D-064). Срок жизни —
 * `screen`: потерянный набор виден сразу и стоит одного ввода, а черновиков,
 * которые нельзя потерять (поставка), здесь нет.
 */
const initialState: PricingIntentState = {
  pairs: {},
  filterAttempts: {},
  comparing: {},
  keysText: {},
  lookup: {},
  priceList: {},
}

function priceListOf(state: PricingIntentState, resourceId: string): PriceListIntent {
  state.priceList[resourceId] ??= { ...EMPTY_PRICE_LIST_INTENT }
  return state.priceList[resourceId]
}

function removeByResource<T>(record: Record<string, T>, resourceId: string): void {
  delete record[resourceId]
}

const slice = createSlice({
  name: 'onec/pricing',
  initialState,
  reducers: {
    /** Другой вид цен — та же выдача поиска с первой страницы. */
    priceListTypeChosen(state, action: PayloadAction<{ resourceId: string; priceType: string }>) {
      const intent = priceListOf(state, action.payload.resourceId)
      intent.priceType = action.payload.priceType === '' ? null : action.payload.priceType
      intent.page = 0
    },
    priceListSearchChanged(state, action: PayloadAction<{ resourceId: string; text: string }>) {
      priceListOf(state, action.payload.resourceId).search = action.payload.text.slice(0, PRICE_LIST_MAX_SEARCH)
    },
    /** Поиск уходит в службу по Enter или «Найти»; `text` — сразу задать набранное (очистка, восстановление вкладки). */
    priceListSearchSubmitted(state, action: PayloadAction<{ resourceId: string; text?: string }>) {
      const intent = priceListOf(state, action.payload.resourceId)
      if (action.payload.text !== undefined) intent.search = action.payload.text.slice(0, PRICE_LIST_MAX_SEARCH)
      intent.query = intent.search.trim()
      intent.page = 0
    },
    priceListPageChanged(state, action: PayloadAction<{ resourceId: string; page: number }>) {
      priceListOf(state, action.payload.resourceId).page = Math.max(0, Math.trunc(action.payload.page))
    },
    pairChanged(
      state,
      action: PayloadAction<{ resourceId: string; field: keyof PairDraft; value: string; saved: PairDraft | null }>,
    ) {
      const { resourceId, field, value, saved } = action.payload
      const current = state.pairs[resourceId] ?? { first: saved?.first ?? '', second: saved?.second ?? '' }
      state.pairs[resourceId] = { ...current, [field]: value }
      delete state.filterAttempts[resourceId]
    },
    /** Ключи попытки записаны ДО отправки: повтор после обрыва уйдёт с теми же (ADR-11). */
    filterAttemptRecorded(state, action: PayloadAction<{ resourceId: string; attempt: CommandAttempt }>) {
      state.filterAttempts[action.payload.resourceId] ??= action.payload.attempt
    },
    pairDiscarded(state, action: PayloadAction<{ resourceId: string }>) {
      delete state.pairs[action.payload.resourceId]
      delete state.comparing[action.payload.resourceId]
      delete state.filterAttempts[action.payload.resourceId]
    },
    /** «Сверить»: показать расхождения для этой пары (набранной, ещё не сохранённой). */
    compareRequested(state, action: PayloadAction<{ resourceId: string; pair: PairDraft | null }>) {
      state.comparing[action.payload.resourceId] = action.payload.pair
    },
    keysTextChanged(state, action: PayloadAction<{ resourceId: string; text: string }>) {
      state.keysText[action.payload.resourceId] = action.payload.text
    },
    lookupRequested(state, action: PayloadAction<{ resourceId: string; keys: readonly string[] }>) {
      state.lookup[action.payload.resourceId] = [...action.payload.keys]
    },
    lookupCleared(state, action: PayloadAction<{ resourceId: string }>) {
      delete state.lookup[action.payload.resourceId]
    },
    /** Вкладка вернула набранное по ресурсу. */
    restored(state, action: PayloadAction<{ resourceId: string; snapshot: PricingSnapshot }>) {
      const { resourceId, snapshot } = action.payload
      if (snapshot.pair !== null) state.pairs[resourceId] = snapshot.pair
      state.keysText[resourceId] = snapshot.keysText
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(userScopeCleared, () => initialState)
      .addCase(resourceScopeCleared, (state, action) => {
        const { resourceId } = action.payload
        removeByResource(state.pairs, resourceId)
        removeByResource(state.filterAttempts, resourceId)
        removeByResource(state.comparing, resourceId)
        removeByResource(state.keysText, resourceId)
        removeByResource(state.lookup, resourceId)
        removeByResource(state.priceList, resourceId)
      })
      .addMatcher(onecSavePriceFilter.matchFulfilled, (state, action) => {
        const { resourceId } = action.meta.arg.originalArgs
        delete state.pairs[resourceId]
        delete state.filterAttempts[resourceId]
        delete state.comparing[resourceId]
      })
  },
})

export const {
  pairChanged,
  filterAttemptRecorded,
  pairDiscarded,
  compareRequested,
  keysTextChanged,
  lookupRequested,
  lookupCleared,
  restored,
  priceListTypeChosen,
  priceListSearchChanged,
  priceListSearchSubmitted,
  priceListPageChanged,
} = slice.actions
export const pricingReducer = slice.reducer

export function priceListIntentOf(state: PricingIntentState, resourceId: string): PriceListIntent {
  return state.priceList[resourceId] ?? EMPTY_PRICE_LIST_INTENT
}

/** Что вкладка помнит об окне «Цены и остатки» ресурса. */
export interface PricingSnapshot {
  pair: PairDraft | null
  keysText: string
}

export function capturePricing(state: PricingIntentState, resourceId: string): PricingSnapshot {
  return { pair: state.pairs[resourceId] ?? null, keysText: state.keysText[resourceId] ?? '' }
}

/** Проверка запомненного вкладкой (D-072): битое — `undefined`. */
export function decodePricingSnapshot(raw: unknown): PricingSnapshot | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const value = raw as { pair?: unknown; keysText?: unknown }
  if (typeof value.keysText !== 'string') return undefined
  let pair: PairDraft | null = null
  if (value.pair !== null && value.pair !== undefined) {
    const candidate = value.pair as { first?: unknown; second?: unknown }
    if (typeof candidate.first !== 'string' || typeof candidate.second !== 'string') return undefined
    pair = { first: candidate.first.slice(0, 64), second: candidate.second.slice(0, 64) }
  }
  return { pair, keysText: value.keysText.slice(0, 40_000) }
}
